import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import useApi from "../api-manager/Helper/useApi";
import AuthContext from "../secure-route/AuthContext";

/**
 * Mirrors the gateway's Permission Group enforcement (see backend
 * api/StoreMembership/access.py -> can_perform_service_action) so the UI can
 * hide/disable actions a user isn't allowed to do, instead of only finding
 * out from a 403 after clicking.
 *
 * Rules (kept in sync with the backend):
 *  - Superusers: always allowed.
 *  - No active store membership found: allowed (legacy/unconfigured account).
 *  - role === "owner": always allowed.
 *  - Membership has no permission_groups and no direct_permissions yet:
 *    allowed (Permission Groups haven't been turned on for this person).
 *  - Otherwise: allowed only if "<module>.<action>" (or "*") is present in
 *    the membership's effective_permissions.
 */
const useMyPermissions = () => {
  const { Get } = useApi();
  const { user } = useContext(AuthContext);
  const storeId = user?.store;
  const userId = user?.user_id || user?.id;

  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMembership = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Get("storeMemberships");
      const list = Array.isArray(res?.results) ? res.results : (Array.isArray(res) ? res : []);
      // Owners/admins get every membership in the store back from this endpoint
      // (not just their own), so we must match on user too - matching by store
      // alone would silently pick a random employee's row instead of ours.
      const mine = list.find((m) => {
        const sameStore = String(m.store) === String(storeId);
        const sameUser = userId == null || String(m.user) === String(userId);
        return sameStore && sameUser;
      }) || (list.length === 1 ? list[0] : null);
      setMembership(mine);
    } catch (err) {
      // If this fails (e.g. legacy account with no membership yet), fail open
      // rather than locking the user out of the UI entirely.
      console.error("Error loading my permissions:", err);
      setMembership(null);
    } finally {
      setLoading(false);
    }
  }, [Get, storeId]);

  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  const isConfigured = !!membership && (
    (membership.permission_groups && membership.permission_groups.length > 0) ||
    (membership.direct_permissions && membership.direct_permissions.length > 0)
  );

  const can = useCallback((module, action) => {
    if (user?.is_superuser) return true;
    if (!membership) return true; // no membership record yet - don't block legacy accounts
    if (membership.role === "owner") return true;
    if (!isConfigured) return true; // not opted into Permission Groups yet

    const effective = membership.effective_permissions || [];
    const code = `${module}.${action}`;
    if (effective.includes("*") || effective.includes(code)) return true;

    // A "view" check also covers apps restricted to specific pages instead
    // of the "All pages in this app" toggle: if the group grants even one
    // page inside this module, the app itself must still show up in the
    // switcher/launcher - otherwise "view just this one page" would make
    // the whole app disappear, which is the opposite of what was granted.
    if (action === "view") {
      const pagePrefix = `${module}:page:`;
      return effective.some((c) => c.startsWith(pagePrefix));
    }

    return false;
  }, [membership, isConfigured, user]);

  /**
   * Page-level check, for restricting which pages inside an app a member
   * can open (as opposed to `can`, which gates whole-module CRUD actions).
   * Page codes look like "<module>:page:<path>.<action>" (see
   * PermissionGroupsSettings.js). Groups saved before this feature existed
   * only ever set the module-wide "<module>.<action>" code, so: if a group
   * has NO page-specific codes for this module at all, fall back to the
   * module-wide check (unchanged behavior). Only once an owner checks at
   * least one individual page for a module does per-page restriction kick
   * in for that module - unchecked pages then become inaccessible.
   */
  const canAccessPage = useCallback((module, pagePath, action = "view") => {
    if (user?.is_superuser) return true;
    if (!membership) return true;
    if (membership.role === "owner") return true;
    if (!isConfigured) return true;

    const effective = membership.effective_permissions || [];
    if (effective.includes("*")) return true;

    const pagePrefix = `${module}:page:`;
    const hasAnyPageCodesForModule = effective.some((c) => c.startsWith(pagePrefix));

    if (hasAnyPageCodesForModule) {
      return effective.includes(`${pagePrefix}${pagePath}.${action}`);
    }

    // No page-level grants configured for this module - defer to the
    // module-wide "All pages in this app" check.
    return effective.includes(`${module}.${action}`);
  }, [membership, isConfigured, user]);

  const isOwnerOrAdmin = user?.is_superuser || membership?.role === "owner" || membership?.role === "admin";

  return useMemo(() => ({
    loading,
    membership,
    can,
    canAccessPage,
    isConfigured,
    isOwnerOrAdmin,
    refresh: fetchMembership,
  }), [loading, membership, can, canAccessPage, isConfigured, isOwnerOrAdmin, fetchMembership]);
};

export default useMyPermissions;
